import React, { useEffect, useMemo, useRef, useState } from "react";
import * as FeatherIcon from "react-feather";
import "./AllClients.css";
import AccountLogo from "./AccountLogo";
import { useCompetitorContext } from "../../../context/CompetitorContext";
import { useNavigate } from "react-router-dom";
import { fetchClientsIndustriesRequest } from "../../../store/features/actions/fetchClientIndustriesCompetitorsActions";
import { useDispatch, useSelector } from "react-redux";

const REPORT_TYPE_CONFIG = {
  AUTO: { label: "AUTO", className: "type-auto" },
  TAM: { label: "TAM", className: "type-tam" },
  COMP_PROFILE: { label: "COMP-PROF", className: "type-comp" },
  COMP_LANDSCAPE: { label: "COMP-LAND", className: "type-comp" },
  DEEP_DIVE: { label: "MKT-DEEP", className: "type-mkt" },
  MARKET_ENTRY: { label: "MKT-ENT", className: "type-mkt" },
  GO_TO_MARKET: { label: "GTM", className: "type-gtm" },
  GO_NO_GO: { label: "GNG", className: "type-gng" },
  VOICE_OF_CUSTOMER: { label: "VOC", className: "type-voc" },
  MA_PARTNER: { label: "M&A", className: "type-ma" },
  ACCOUNT_IDENTITY: { label: "ACC-ID", className: "type-acci" },
  WHITEPAPER: { label: "WP", className: "type-wp" },
};

/* ─── helpers ─── */
function formatResearchType(type) {
  if (!type) return null;
  const upperType = type.toUpperCase();
  if (REPORT_TYPE_CONFIG[upperType]) {
    return REPORT_TYPE_CONFIG[upperType].label;
  }
  // Fallback: Remove underscores and capitalize
  return type
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function relativeTime(dateStr) {
  if (!dateStr) return null;
  const now = new Date();
  const d = new Date(dateStr);
  const diffMs = now - d;
  if (diffMs < 0) return "just now";
  const mins = Math.floor(diffMs / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function badgeColorClass(rawType) {
  if (!rawType) return "";
  const upperType = rawType.toUpperCase();
  if (REPORT_TYPE_CONFIG[upperType]) {
    return REPORT_TYPE_CONFIG[upperType].className;
  }
  return "";
}

const DUMMY_RESPONSE = {
  data: [
    {
      industry: null,
      clients: [
        {
          client: "MnM AllDomains",
          companys: ["The Boeing Company", "Wacker Chemie AG", "Elkam As"],
          company_count: 3,
        },
        {
          client: "H.B. Fuller",
          companys: [
            "Tailored Chemical Products",
            "Henkel",
            "Bostik",
            "Sika A.G.",
            "3M Company",
          ],
          company_count: 5,
        },
      ],
    },
    {
      industry: "test industry",
      clients: [
        {
          client: "test_client",
          companys: ["test competitor"],
          company_count: 1,
        },
      ],
    },
  ],
};

export default function AllClients() {
  const navigate = useNavigate();
  /* -------------------- UI state -------------------- */
  const dispatch = useDispatch();
  const [search, setSearch] = useState("");
  const [selectedIndustryIndex, setSelectedIndustryIndex] = useState(0);
  const [selectedClientIndex, setSelectedClientIndex] = useState(0);
  const [selectedDeepResearch, setSelectedDeepResearch] = useState(0);

  // Toggle
  const [isToggled, setIsToggled] = useState(false);

  /* Filter dropdown shell */
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { competitorInfo, setCompetitorInfo } = useCompetitorContext();

  // const rawData = DUMMY_RESPONSE.data;
  const { loading, data, error } = useSelector(
    (state) => state.fetchClientIndustriesCompetitorsReducer
  );

  /**
   * Count unique completed deep-research reports for a client.
   */
  const getDeepResearchCount = (client, query = "") => {
    const reports = client?.reports || [];
    const seen = new Set();
    const tokens = query
      .toLowerCase()
      .split(/\s+/)
      .map((t) => t.trim())
      .filter(Boolean);
    let count = 0;
    for (const r of reports) {
      const st = (r.status || "").toUpperCase();
      if (st !== "COMPLETED" && st !== "COMPLETE") continue;
      const title = (r.user_input || "").trim();
      if (!title || seen.has(title)) continue;
      if (tokens.length) {
        const type = formatResearchType(r.research_type)?.toLowerCase() || "";
        const planId = (r.plan_id || "").toString().toLowerCase();
        const searchableText = `${title.toLowerCase()} ${type} ${planId}`;
        if (!tokens.every((token) => searchableText.includes(token))) continue;
      }
      seen.add(title);
      count++;
    }
    return count;
  };

  // const rawData = data?.data || [];
  // const rawData = (data?.data || []).filter(
  //   (item) => item.industry !== null && item.industry !== ""
  // );
  // const rawData = (data?.data || [])
  //   .filter((item) => item.industry !== null && item.industry !== "")
  //   .map((industry) => {
  //     const filteredClients = industry.clients.filter((client) => {
  //       if (isToggled) {
  //         return getDeepResearchCount(client) > 0;
  //       }
  //       return (client.display_company_name ? 1 : companys.length || client.companys || []).length > 0;
  //     });

  //     return {
  //       ...industry,
  //       clients: filteredClients,
  //     };
  //   })
  //   .filter((industry) => industry.clients.length > 0);
  const rawData = (data?.data || [])
  .filter((item) => item.industry !== null && item.industry !== "")
  .map((industry) => {
    const filteredClients = industry.clients.filter((client) => {
      if (isToggled) {
        return getDeepResearchCount(client) > 0;
      }

      const companyCount = client.display_company_name
        ? 1
        : (client.companys || []).length;

      return companyCount > 0;
    });

    return {
      ...industry,
      clients: filteredClients,
    };
  })
  .filter((industry) => industry.clients.length > 0);

  /**
   * GLOBAL SEARCH
   * Search applies across:
   * - industry
   * - client
   * - competitor (when Competitors view)
   * - report title / research_type (when Deep Research view)
   */
  const filteredData = useMemo(() => {
    if (!search) return rawData;

    const q = search.toLowerCase();
    const tokens = search
      .toLowerCase()
      .split(/\s+/)
      .map((t) => t.trim())
      .filter(Boolean);

    return rawData
      .map((industryBlock) => {
        const industryMatch = industryBlock.industry?.toLowerCase().includes(q);

        const matchedClients = industryBlock.clients
          .map((client) => {
            const clientMatch = client.client.toLowerCase().includes(q);

            if (isToggled) {
              /* Deep Research mode: match against report titles & types */
              // const reports = client.reports || [];
              // const hasMatchingReport = reports.some((r) => {
              //   const st = (r.status || "").toUpperCase();
              //   if (st !== "COMPLETED" && st !== "COMPLETE") return false;
              //   const title = (r.user_input || "").trim().toLowerCase();
              //   const type =
              //     formatResearchType(r.research_type)?.toLowerCase() || "";
              //   return title.includes(q) || type.includes(q);
              // });
              const reports = client.reports || [];

              const hasMatchingReport = reports.some((r) => {
                const st = (r.status || "").toUpperCase();
                if (st !== "COMPLETED" && st !== "COMPLETE") return false;

                const title = (r.user_input || "").trim().toLowerCase();
                const type =
                  formatResearchType(r.research_type)?.toLowerCase() || "";
                const planId = (r.plan_id || "").toString().toLowerCase();

                // Combine all searchable fields
                const searchableText = `${title} ${type} ${planId}`;

                // AND condition → ALL words must exist
                return tokens.every((token) => searchableText.includes(token));
              });

              if (industryMatch || clientMatch || hasMatchingReport) {
                return client;
              }
            } else {
              /* Competitor mode: match against company names */
              const matchedCompanies = client.companys.filter((c) =>
                c.company?.toLowerCase().includes(q)
              );

              if (industryMatch || clientMatch || matchedCompanies.length) {
                return {
                  ...client,
                  companys: matchedCompanies.length
                    ? matchedCompanies
                    : client.companys,
                };
              }
            }
            return null;
          })
          .filter(Boolean);

        return matchedClients.length
          ? { ...industryBlock, clients: matchedClients }
          : null;
      })
      .filter(Boolean);
  }, [rawData, search, isToggled]);

  /* When Deep Research toggle is ON, hide clients with 0 reports */
  const displayData = useMemo(() => {
    if (!isToggled) return filteredData;
    return filteredData
      .map((industryBlock) => {
        const clientsWithReports = industryBlock.clients.filter(
          (client) => getDeepResearchCount(client, search) > 0
        );
        return clientsWithReports.length
          ? { ...industryBlock, clients: clientsWithReports }
          : null;
      })
      .filter(Boolean);
  }, [filteredData, isToggled, search]);

  const selectedIndustry = displayData[selectedIndustryIndex] || displayData[0];

  const selectedClient =
    selectedIndustry?.clients?.[selectedClientIndex] ??
    selectedIndustry?.clients?.[0] ??
    null;

  
  // Select the display name for the client: prefer display_company_name, fallback to client
  const clientName = selectedClient?.display_company_name || selectedClient?.client;

  /* ─── Deep Research: cards for the selected client (filtered by industry → client) ─── */
  const clientDeepResearch = useMemo(() => {
    const reports = selectedClient?.reports || [];
    const seen = new Set();
    const cards = [];
    for (const r of reports) {
      const st = (r.status || "").toUpperCase();
      if (st !== "COMPLETED" && st !== "COMPLETE") continue;
      const title = (r.user_input || "").trim();
      if (!title || seen.has(title)) continue;
      seen.add(title);
      const formattedType = formatResearchType(r.research_type);
      const ago = relativeTime(r.completed_at);
      const card = {
        _id: r._id,
        title,
        raw_research_type: r.research_type,
        plan_id: r.plan_id || "",
      };
      if (formattedType) card.research_type = formattedType;
      if (ago) card.relativeTime = ago;
      if (r.completed_at) card.completed_at = r.completed_at;
      cards.push(card);
    }
    /* sort by date descending; no-date items last */
    cards.sort((a, b) => {
      if (a.completed_at && b.completed_at)
        return new Date(b.completed_at) - new Date(a.completed_at);
      if (a.completed_at) return -1;
      if (b.completed_at) return 1;
      return 0;
    });
    /* apply search filter */
    if (!search) return cards;
    const tokens = search
      .toLowerCase()
      .split(/\s+/)
      .map((t) => t.trim())
      .filter(Boolean);
    return cards.filter((c) => {
      const searchableText = `${c.title} ${c.research_type || ""} ${
        c.plan_id || ""
      }`.toLowerCase();
      return tokens.every((token) => searchableText.includes(token));
    });
  }, [selectedClient, search]);

  /* Close filter dropdown on outside click */
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // const handleViewProfile = (company) => {
  //   // setting the competitor info for competitor context
  //   setCompetitorInfo({
  //     client: selectedClient,
  //     competitor_name: company,
  //     vendor_company_id: company?.vendor_company_id ?? "",
  //     ks_companies: company?.ks_companies ?? [],
  //   });
  //   localStorage.removeItem("localStorageCompetitorInfo");
  //   localStorage.setItem(
  //     "localStorageCompetitorInfo",
  //     JSON.stringify({
  //       client: selectedClient,
  //       competitor_name: company,
  //       vendor_company_id: company?.vendor_company_id ?? "",
  //       ks_companies: company?.ks_companies ?? [],
  //     })
  //   );
  //   navigate(
  //     `/comp_profile/${selectedClient?.client}/${encodeURIComponent(company)}`
  //   );
  // };

  const handleViewProfile = (companyObj) => {
    const competitorName = companyObj?.display_company_name || companyObj?.company;
    setCompetitorInfo({
      // client: selectedClient?.client,
      client: clientName,
      competitor_display_name: competitorName,
      competitor_name: companyObj?.company,
      vendor_company_id: companyObj?.vendor_company_id ?? "",
      ks_companies: companyObj?.ks_companies ?? [],
    });

    localStorage.setItem(
      "localStorageCompetitorInfo",
      JSON.stringify({
        // client: selectedClient?.client,
        client: clientName,
        competitor_display_name: competitorName,
        competitor_name: companyObj?.company,
        vendor_company_id: companyObj?.vendor_company_id ?? "",
        ks_companies: companyObj?.ks_companies ?? [],
      })
    );
    localStorage.setItem("selectedClient", "");
    localStorage.setItem("selectedIndustry", "");

    // localStorage.setItem("selectedClient", selectedClient?.client);
    localStorage.setItem("selectedClient", clientName);

    localStorage.setItem("selectedIndustry", selectedIndustry?.industry);

    navigate(
      `/comp_profile/${encodeURIComponent(
        // selectedClient?.client
        clientName
      )}/${encodeURIComponent(companyObj?.company)}`
    );
  };

  const handleViewDeepResearch = (card) => {
    localStorage.removeItem("reportId");
    localStorage.removeItem("reportType");
    localStorage.setItem("reportId", card?._id ?? "");
    localStorage.setItem("reportType", card?.raw_research_type ?? "");
    navigate("/watchboard");
  };

  // useEffect(() => {
  //   setSelectedIndustryIndex(0);
  //   setSelectedClientIndex(0);
  // }, [filteredData]);

  useEffect(() => {
    setSelectedClientIndex(0);
  }, [selectedIndustryIndex]);

  useEffect(() => {
    setSelectedIndustryIndex(0);
    setSelectedClientIndex(0);
  }, [search, isToggled]);

  useEffect(() => {
    dispatch(fetchClientsIndustriesRequest());
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  /* -------------------- RENDER -------------------- */
  return (
    <div>
      <div className="new_login_page">
        <div className="container-fluid sales-play-demo-accounts-page">
          {/* SEARCH + TOGGLE BAR */}
          <div className="row mb-3 align-items-center">
            <div className="col-md-6">
              {/* <div
                    className="new_search_custom mr-4"
                    style={{ width: "85px", display: "inline-block" }}
                  >
                    <div className="filter-container" ref={dropdownRef}>
                      <div className="filter-dropdown">
                        <button
                          className="custom-dropdown-toggle"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setIsDropdownOpen((p) => !p);
                          }}
                        >
                          Filter
                          <img src="/img/chevron-down.svg" />
                        </button>

                        {isDropdownOpen && (
                          <div className="dropdown-content show">
                            <div className="p-2 text-muted">
                              Filters coming soon
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div> */}

              <div
                className="new_search_custom ml-2 float-start"
                style={{ display: "inline-block" }}
              >
                <input
                  type="text"
                  style={{ minWidth: "350px" }}
                  className="sales_search_input"
                  placeholder={
                    isToggled
                      ? "Search by Deep Research"
                      : "Search by Seller Companies or Target Account"
                  }
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  autoComplete="off"
                />
                <FeatherIcon.Search className="seach_new_login" />
              </div>
            </div>

            {/* Comment toggle*/}
            <div className="col-md-6 d-flex justify-content-end">
              <div className="segmented-toggle">
                <button
                  className={`seg-btn ${!isToggled ? "seg-active" : ""}`}
                  onClick={() => setIsToggled(false)}
                >
                  Competitors
                </button>
                <button
                  className={`seg-btn ${isToggled ? "seg-active" : ""}`}
                  onClick={() => setIsToggled(true)}
                >
                  Deep Research
                </button>
              </div>
            </div>
          </div>

          {/* THREE PANE LAYOUT */}
          <div className="custom_card_block">
            <div className="three_block_flex">
              {/* CLIENT INDUSTRY */}
              <div className="industry_block">
                <div className="div_head">
                  <h5 className="head">Client Industry</h5>
                </div>

                <div className="list_ind_block">
                  <ul className="list_trends_ul">
                    {displayData.map((item, index) => (
                      <li
                        key={index}
                        className={`list_trends_li ${
                          index === selectedIndustryIndex ? "active" : ""
                        }`}
                        onClick={() => {
                          setSelectedIndustryIndex(index);
                          setSelectedClientIndex(null);
                        }}
                      >
                        <span className="trend_name">
                          {item.industry} ({item.clients.length})
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* CLIENT COMPANY */}
              <div className="clients_block">
                <div className="div_head">
                  <h5 className="head">Client Company</h5>
                </div>

                <div className="client_list_ind_block">
                  <ul className="client_list_trends_ul">
                    {/* {selectedIndustry?.clients?.map((client, index) => (
                      <li
                        key={index}
                        className={`client_list_trends_li ${
                          index === selectedClientIndex ? "active" : ""
                        }`}
                        onClick={() => setSelectedClientIndex(index)}
                      >
                        <span className="client_trend_name">
                          {/* {client.client} ( */}
                          {/* {client.display_company_name || client.client} {/* {isToggled */}
                            {/* ? getDeepResearchCount(client)
                            : client.company_count}
                          ) */}
                          {/* {isToggled
                            ? getDeepResearchCount(client, search)
                            : (client.display_company_name ? 1 : companys.length || client.companys || []).length}
                          ) */}
                        {/* </span>
                      </li>
                    ))} */} 
                    {selectedIndustry?.clients?.map((client, index) => {
                      const companyCount = client.display_company_name
                        ? 1
                        : (client.companys || []).length;

                      return (
                        <li
                          key={index}
                          className={`client_list_trends_li ${
                            index === selectedClientIndex ? "active" : ""
                          }`}
                          onClick={() => setSelectedClientIndex(index)}
                        >
                          <span className="client_trend_name">
                            {client.display_company_name || client.client} (
                            {isToggled
                              ? getDeepResearchCount(client, search)
                              : companyCount}
                            )
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>

              {/* RIGHT PANE: Competitors OR Deep Research */}
              <div className="sales_block">
                <div className="div_head">
                  <h5 className="head">
                    {isToggled ? "Deep Research" : "Competitors"}
                  </h5>
                </div>

                <div className="sales_list_block">
                  {isToggled ? (
                    /* ── Deep Research cards ── */
                    clientDeepResearch.length === 0 ? (
                      <div className="text-center py-4 text-muted">
                        No deep research reports found.
                      </div>
                    ) : (
                      <div className="dr-cards-grid">
                        {clientDeepResearch.map((card) => (
                          <div
                            key={card._id}
                            className="dr-card"
                            onClick={() => handleViewDeepResearch(card)}
                          >
                            {/* header row: time + badge */}
                            <div className="dr-card-header">
                              {card.relativeTime && (
                                <span className="dr-time">
                                  {card.relativeTime}
                                </span>
                              )}
                              <div className="dr-badges">
                                {card.research_type && (
                                  <span
                                    className={`dr-badge ${badgeColorClass(
                                      card.raw_research_type
                                    )}`}
                                  >
                                    {card.research_type}
                                  </span>
                                )}
                              </div>
                            </div>

                            {/* title — expands on hover */}
                            <p className="dr-title">{card.title}</p>
                          </div>
                        ))}
                      </div>
                    )
                  ) : (
                    /* ── Competitor cards ── */
                    <div className="row">
                      {selectedClient?.companys?.length ? (
                        selectedClient.companys.map((company, index) => (
                          <div
                            key={index}
                            className={`col-md-3 ${
                              company?.generating ? "" : "cursorP"
                            }`}
                          >
                            <div
                              className={`logo_block ${
                                company?.generating ? "disabled" : ""
                              }`}
                              onClick={() => {
                                if (!company?.generating) {
                                  handleViewProfile(company);
                                }
                              }}
                            >
                              <AccountLogo
                                key={`account_img_comp_${company?.company}`}
                                account_name={company?.company}
                              />

                              <div className="com_name_block">
                                <h5 className="comp_name">
                                  <span className="account_title_link">
                                    {/* {company?.company} */}
                                    {company?.display_company_name || company?.company}
                                    {company?.generating && (
                                      <span className="ml-1 text-muted">
                                        (Generating…)
                                      </span>
                                    )}
                                  </span>
                                </h5>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="col-md-12 text-center py-4">
                          No competitors added yet.
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
